import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { navItems, site } from "../content";
import { scrollToId, scrollToTop } from "../lib/scroll";

export const Nav = () => {
  const [onLight, setOnLight] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("hero");
      const threshold = hero ? hero.offsetHeight - window.innerHeight * 0.6 : window.innerHeight;
      setOnLight(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const ink = onLight ? "text-[#1A1A1A]" : "text-white";
  const subInk = onLight ? "text-black/50" : "text-white/60";
  const badge = onLight
    ? "border-black/15 bg-white/70 text-[#1A1A1A]"
    : "border-white/20 bg-white/10 text-white";

  return (
    <>
      <a
        href="#projetos"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-black focus:px-4 focus:py-2 focus:text-xs focus:text-white"
      >
        Pular para o conteúdo
      </a>
      <header className="pointer-events-none fixed inset-x-0 top-0 z-50" data-testid="site-nav">
        <div className="relative flex items-start justify-between px-4 pt-4 md:px-8 md:pt-5">
          <button
            type="button"
            data-testid="nav-logo"
            onClick={() => {
              setOpen(false);
              scrollToTop();
            }}
            aria-label="Lucas Rocha — voltar ao topo"
            className={`pointer-events-auto flex items-center gap-3 text-left transition-colors duration-500 ${ink}`}
          >
            <span
              className={`grid h-9 w-9 place-items-center rounded-full border font-display text-xs font-extrabold tracking-tight backdrop-blur transition-colors duration-500 ${badge}`}
            >
              LR
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm font-bold tracking-tight">{site.name}</span>
              <span className={`block font-mono text-[9px] uppercase tracking-[0.22em] transition-colors duration-500 ${subInk}`}>
                {site.role}
              </span>
            </span>
          </button>

          <nav
            data-testid="nav-pill"
            aria-label="Navegação principal"
            className="pointer-events-auto absolute left-1/2 top-4 hidden -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-black/80 px-2 py-1.5 shadow-2xl backdrop-blur-xl md:top-5 lg:flex"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`nav-link-${item.id}`}
                onClick={() => go(item.id)}
                className="rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="pointer-events-auto flex items-center gap-2">
            <button
              type="button"
              data-testid="nav-cta"
              onClick={() => go("contato")}
              className="group hidden items-center gap-3 rounded-full bg-white py-1.5 pl-5 pr-1.5 shadow-xl transition-colors duration-300 hover:bg-[#00E5FF] sm:flex"
            >
              <span className="font-display text-xs font-bold uppercase tracking-wide text-black">Vamos criar algo juntos</span>
              <span className="grid h-7 w-7 place-items-center rounded-full bg-black text-white transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={13} />
              </span>
            </button>
            <button
              type="button"
              data-testid="nav-menu-button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              aria-expanded={open}
              className={`grid h-10 w-10 place-items-center rounded-full border backdrop-blur transition-colors duration-300 lg:hidden ${badge} ${ink}`}
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0B2B46]/95 px-8 backdrop-blur-xl"
            data-testid="nav-mobile-overlay"
          >
            <nav aria-label="Navegação móvel" className="space-y-2">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.id}
                  type="button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  data-testid={`nav-mobile-link-${item.id}`}
                  onClick={() => go(item.id)}
                  className="block w-full text-left font-display text-4xl font-extrabold tracking-tighter text-white transition-colors hover:text-[#00E5FF]"
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.36, duration: 0.4 }}
              data-testid="nav-mobile-cta"
              onClick={() => go("contato")}
              className="mt-10 flex w-fit items-center gap-3 rounded-full bg-white py-2 pl-6 pr-2"
            >
              <span className="font-display text-xs font-bold uppercase tracking-wide text-black">Vamos criar algo juntos</span>
              <span className="grid h-8 w-8 place-items-center rounded-full bg-black text-white">
                <ArrowUpRight size={14} />
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
